import { FC, useMemo } from 'react';
import { Alert, Select, Space } from 'antd';
import type { AlertProps } from 'antd';
import { FormBannerDescription } from '#types/System.Parameters.types';

interface BannerOption {
    label: string;
    value: string;
    alertType?: AlertProps['type'];
}

/**
 * Props for the FormBannerUI component
 */
interface FormBannerUIProps {
    /** Current banner type value */
    value: string;
    /** Callback when banner type changes */
    onChange: (value: string) => void;
    /** Whether the control is read-only */
    disabled?: boolean;
    /** Optional message to display in the banner */
    message?: string;
    /** Style object for the component */
    style?: React.CSSProperties;
    /** Custom options for the banner */
    options?: BannerOption[];
}

/**
 * A form banner component that displays an Alert with a Select dropdown
 * to choose different banner types with associated colors.
 */
const FormBannerUI: FC<FormBannerUIProps> = ({
    value = 'none',
    onChange,
    disabled = false,
    message,
    style,
    options: customOptions
}) => {
    const options = useMemo(() => {
        if (customOptions && customOptions.length > 0) {
            return customOptions.map(opt => ({
                value: opt.value,
                label: opt.label
            }));
        }
        
        return Object.entries(FormBannerDescription).map(([key, desc]) => ({
            value: key,
            label: desc.shortLabel
        }));
    }, [customOptions]);

    const currentOption = useMemo(() => {
        if (customOptions && customOptions.length > 0) {
            return customOptions.find(opt => opt.value === value);
        }
        return FormBannerDescription[value as keyof typeof FormBannerDescription];
    }, [value, customOptions]);

    const displayMessage = message || (
        currentOption && 'longDescription' in currentOption 
            ? currentOption.longDescription 
            : ''
    );

    const alertStyle = useMemo(() => {
        if (value === 'none') {
            return {
                background: 'transparent',
                borderColor: 'transparent',
                width: '100%',
                ...style
            };
        }
        return { width: '100%', ...style };
    }, [value, style]);

    const alertType = useMemo(() => {
        if (value === 'none') return 'info';
        
        if (customOptions && currentOption && 'alertType' in currentOption) {
            return currentOption.alertType || 'info';
        }
        
        return value as AlertProps['type'];
    }, [value, customOptions, currentOption]);

    return (
        <Alert
            title={
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <span>{displayMessage}</span>
                    <Select
                        variant='borderless'
                        value={value}
                        onChange={onChange}
                        disabled={disabled}
                        options={options}
                        styles={{
                            popup: {
                                root: {
                                    width: 200
                                }
                            }
                        }}
                    />
                </Space>
            }
            type={alertType}
            style={alertStyle}
        />
    );
};

export default FormBannerUI; 